import { type CSSProperties, type ReactNode } from "react";
import { GripVertical } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

interface SortableShellProps {
  sectionKey: string;
  /** Display label for the drag header. */
  label: string;
  /** True when the user is currently dragging this card. The
   *  source row dims to opacity-30 so the cursor-following
   *  DragOverlay reads as the active item. */
  isDragging?: boolean;
  children: ReactNode;
}

/**
 * Edit-mode card wrapper for the Company-detail layout. Provides
 * the visual frame in edit mode (a single white card with a drag
 * header), truncates the section body to a 150px preview with a
 * gradient fade so the layout doesn't jump as the operator drags
 * cards around.
 *
 * The whole top bar is the drag activator — generous hit target,
 * matches the affordance from PipelineSection / TaskKanbanBoard.
 * `isDragging` is driven externally (active id from DndContext)
 * rather than via useSortable's own flag because the DragOverlay
 * makes the sortable item itself become invisible at drop time;
 * the external flag keeps the source row visible-but-dimmed for
 * the duration of the drag.
 */
export function SortableShell({
  sectionKey,
  label,
  isDragging,
  children,
}: SortableShellProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sectionKey });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    // Smooth gap-shift when neighbouring cards reflow during drag.
    transition: transition ?? "transform 200ms ease",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "rounded-xl bg-white transition-all duration-150",
        isDragging
          ? "scale-[0.98] opacity-30 shadow-card-open"
          : "shadow-card-rest",
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className="flex cursor-grab items-center gap-2 rounded-t-xl border-b border-slate-900/[0.06] px-4 py-2.5 text-slate-500 transition-colors hover:bg-slate-50/60 active:cursor-grabbing"
      >
        <GripVertical className="h-4 w-4 text-slate-300" />
        <span className="text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="relative max-h-[150px] overflow-hidden p-4">
        <div className="pointer-events-none opacity-60">{children}</div>
        {/* Bottom fade so truncated content reads as
         *  "more below" rather than abruptly clipped. */}
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"
        />
      </div>
    </div>
  );
}
